<template lang="pug">
v-menu(v-model="open" :activator="menuActivator" offset-y left :close-on-content-click="false" max-width="400" content-class="table-constructor-menu")
  template(v-if="!activator" #activator="{ on: menuOn, attrs }")
    v-tooltip(bottom open-delay="200")
      template(#activator="{ on: hintOn }")
        v-btn.table-constructor-trigger(
          icon
          outlined
          color="primary"
          width="40"
          height="40"
          v-bind="attrs"
          v-on="mergeActivatorListeners(menuOn, hintOn)"
          aria-label="Налаштувати таблицю"
          @click.stop
          @keydown.stop
        )
          v-icon(size="20") mdi-table-cog
      span Налаштувати таблицю
  v-card(@click.stop @keydown.esc="open = false")
    v-card-title.py-2.subtitle-1
      span Налаштування таблиці
      v-spacer
      v-btn(icon small aria-label="Закрити налаштування таблиці" @click="open = false")
        v-icon(size="20") mdi-close
    v-divider
    v-list.table-constructor-list.py-1(dense aria-label="Колонки таблиці")
      template(v-for="(column, index) in columns")
        v-divider(v-if="index > 0 && startsGroup(index)" :key="'divider-' + pinValue(column.pin)").my-1
        v-subheader(
          v-if="startsGroup(index)"
          :key="'heading-' + pinValue(column.pin)"
          :class="{ 'primary--text': !!column.pin }"
        ).table-constructor-group-heading
          v-icon(size="14" :color="column.pin ? 'primary' : undefined").mr-2 {{ column.pin ? 'mdi-pin-outline' : 'mdi-pin-off-outline' }}
          span {{ groupLabel(column.pin) }}
        v-list-item(
          :key="column.value"
          :class="{ 'table-constructor-row--locked': column.constructorLocked, 'table-constructor-row--dragging': dragged === column.value, 'table-constructor-row--target': target === column.value }"
          :aria-disabled="column.constructorLocked ? 'true' : null"
          :title="column.constructorLocked ? lockedHint : null"
          @dragover.prevent="dragOver(column, $event)"
          @drop.prevent="drop(index)"
        ).table-constructor-row
          span.table-constructor-drag(
            :draggable="!column.constructorLocked"
            :title="column.constructorLocked ? lockedHint : 'Перетягнути колонку'"
            @dragstart="startDrag($event, column.value)"
            @dragend="endDrag"
          )
            v-icon(size="18") mdi-drag-vertical
          div.table-constructor-order
            v-tooltip(v-for="direction in [-1, 1]" :key="direction" bottom open-delay="200")
              template(#activator="{ on, attrs }")
                span.table-constructor-order-control(
                  v-bind="attrs"
                  v-on="on"
                  :tabindex="!column.constructorLocked && !canMove(index, index + direction) ? 0 : null"
                  :aria-label="moveHint(index, direction) + ' «' + column.text + '»'"
                  @focusin="on.focus"
                  @focusout="on.blur"
                )
                  v-btn(
                    icon
                    x-small
                    :disabled="!canMove(index, index + direction)"
                    :aria-label="'Перемістити «' + column.text + '» ' + (direction < 0 ? 'вище' : 'нижче')"
                    @click="move(index, index + direction)"
                  )
                    v-icon(size="18") {{ direction < 0 ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
              span {{ moveHint(index, direction) }}
          v-checkbox(
            :input-value="column.visible"
            :label="column.text"
            :disabled="column.constructorLocked || (column.visible && visibleCount === 1)"
            hide-details
            dense
            :title="column.constructorLocked ? lockedHint : column.visible && visibleCount === 1 ? 'Залиште видимою хоча б одну колонку' : column.text"
            @change="update(column.value, { visible: $event })"
          ).table-constructor-checkbox.ma-0.pa-0
          v-btn-toggle(:value="pinValue(column.pin)" mandatory dense borderless @change="setPin(column.value, $event)").table-constructor-pin
            v-tooltip(v-for="option in pinOptions" :key="option.value" bottom open-delay="200")
              template(#activator="{ on, attrs }")
                v-btn(:value="option.value" :disabled="column.constructorLocked" icon x-small v-bind="attrs" v-on="on" :aria-label="option.label + ' «' + column.text + '»'")
                  v-icon.table-constructor-pin-direction(v-if="option.value === 'left'" size="10" aria-hidden="true") mdi-arrow-left
                  v-icon(size="18") {{ option.icon }}
                  v-icon.table-constructor-pin-direction(v-if="option.value === 'right'" size="10" aria-hidden="true") mdi-arrow-right
              span {{ option.label }}
    v-divider
    v-card-actions
      v-btn(text small color="primary" @click="$emit('reset')") Відновити початковий вигляд
</template>

<script>
export default {
  name: 'TableConstructor',
  props: {
    columns: { type: Array, required: true },
    activator: { type: String, default: '' }
  },
  data () {
    return {
      open: false, dragged: null, target: null, menuActivator: null,
      lockedHint: 'Цю колонку не можна приховати, перемістити або змінити її закріплення',
      pinOptions: [
        { value: 'left', label: 'Закріпити ліворуч', icon: 'mdi-pin-outline' },
        { value: 'none', label: 'Відкріпити колонку', icon: 'mdi-pin-off-outline' },
        { value: 'right', label: 'Закріпити праворуч', icon: 'mdi-pin-outline' }
      ]
    }
  },
  computed: {
    visibleCount () {
      return this.columns.filter(column => column.visible).length
    }
  },
  watch: {
    activator: {
      immediate: true,
      handler () {
        // Header buttons and conditional tables can be inserted in the same update.
        this.$nextTick(() => {
          if (!this._isDestroyed) this.menuActivator = this.activator || null
        })
      }
    },
    open (value) { this.updateActivatorState(value) }
  },
  beforeDestroy () { this.updateActivatorState(false) },
  methods: {
    mergeActivatorListeners (menuOn, hintOn) {
      const listeners = { ...menuOn }
      for (const key of Object.keys(hintOn)) {
        listeners[key] = menuOn[key] ? [].concat(menuOn[key], hintOn[key]) : hintOn[key]
      }
      return listeners
    },
    updateActivatorState (open) {
      if (!this.activator) return
      const button = document.querySelector(this.activator)
      if (button) button.setAttribute('aria-expanded', String(open))
    },
    pinValue (pin) { return pin || 'none' },
    startsGroup (index) {
      return index === 0 || this.columns[index].pin !== this.columns[index - 1].pin
    },
    groupLabel (pin) {
      if (pin === 'left') return 'Закріплені ліворуч'
      if (pin === 'right') return 'Закріплені праворуч'
      return 'Без закріплення'
    },
    update (value, patch) {
      const column = this.columns.find(column => column.value === value)
      if (!column || column.constructorLocked) return
      this.$emit('change', this.columns.map(column => column.value === value ? { ...column, ...patch } : column))
    },
    setPin (value, pin) {
      this.update(value, { pin: pin === 'none' ? null : pin })
    },
    move (from, to) {
      if (!this.canMove(from, to)) return
      const columns = [...this.columns]
      columns.splice(to, 0, columns.splice(from, 1)[0])
      this.$emit('change', columns)
    },
    canMove (from, to) {
      return from !== to && from >= 0 && to >= 0 && from < this.columns.length && to < this.columns.length &&
        this.columns[from].pin === this.columns[to].pin &&
        !this.columns.slice(Math.min(from, to), Math.max(from, to) + 1).some(column => column.constructorLocked)
    },
    moveHint (index, direction) {
      if (this.columns[index].constructorLocked) return this.lockedHint
      if (this.canMove(index, index + direction)) return direction < 0 ? 'Перемістити вище' : 'Перемістити нижче'
      if (this.columns[index + direction]?.constructorLocked) return 'Не можна перемістити через заблоковану колонку'
      return direction < 0 ? 'Перша колонка у групі' : 'Остання колонка у групі'
    },
    dragOver (column, event) {
      const from = this.columns.findIndex(item => item.value === this.dragged)
      const to = this.columns.findIndex(item => item.value === column.value)
      this.target = this.canMove(from, to) ? column.value : null
      if (event.dataTransfer) event.dataTransfer.dropEffect = this.target ? 'move' : 'none'
    },
    startDrag (event, value) {
      const column = this.columns.find(column => column.value === value)
      if (!column || column.constructorLocked) {
        event.preventDefault()
        this.endDrag()
        return
      }
      this.dragged = value
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/plain', value)
    },
    drop (index) {
      if (this.dragged !== null) this.move(this.columns.findIndex(column => column.value === this.dragged), index)
      this.endDrag()
    },
    endDrag () {
      this.dragged = null
      this.target = null
    }
  }
}
</script>

<style lang="sass">
.table-constructor-menu
  width: min(400px, calc(100vw - 24px))
  max-width: min(400px, calc(100vw - 24px)) !important

  .v-btn--icon
    padding: 0 !important

.table-constructor-list
  max-height: min(400px, 60vh)
  overflow-y: auto

.table-constructor-group-heading
  height: 28px !important
  padding: 0 12px !important
  font-weight: 600

.table-constructor-row
  display: grid !important
  grid-template-columns: 16px 40px minmax(0, 1fr) 84px
  align-items: center
  column-gap: 4px
  min-height: 36px !important
  padding: 4px 12px !important

  // Vuetify's flex min-height helper must not become an extra grid row.
  &::after
    display: none

  &--dragging
    opacity: .5

  &--target
    box-shadow: inset 0 2px 0 currentColor

  &--locked
    opacity: .5
    filter: blur(.5px)

    .table-constructor-drag
      cursor: not-allowed

.table-constructor-drag
  display: flex
  align-items: center
  justify-content: center
  cursor: grab

.table-constructor-checkbox
  min-width: 0
  align-self: center

  .v-input__control, .v-input__slot
    min-width: 0

  .v-input__slot
    margin-bottom: 0 !important

  .v-label
    display: block
    min-width: 0
    font-size: 13px
    white-space: nowrap
    overflow: hidden
    text-overflow: ellipsis

.table-constructor-order
  display: flex
  align-items: center

  .v-btn
    flex: 0 0 20px
    min-width: 20px !important
    width: 20px !important
    height: 28px !important

.table-constructor-order-control
  display: inline-flex
  flex: 0 0 20px
  width: 20px
  height: 28px

.table-constructor-pin
  align-self: center

  .v-btn
    min-width: 28px !important
    width: 28px !important
    height: 28px !important

.table-constructor-pin-direction
  flex: 0 0 10px
  width: 10px !important
  height: 10px !important
</style>
